import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Route, Navigate, Routes, useLocation } from 'react-router-dom';
import PrivateRoute from './components/PrivateRoute';
import AuthProvider from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';
import api, { configureAxiosAuth } from './api/axiosConfig';
import LoginPage from './components/LoginPage';
import useSessionPoller from './hooks/useSessionPoller';
import LegalPage from './pages/legal/LegalPage';
import ToastProvider from './components/ToastProvider';

// Dashboard Pages
import SuperAdminDashboardPage from './pages/SuperAdminDashboardPage';
import SessionManagementPage from './pages/superadmin/SessionManagementPage';
import AtcCodeMaintenancePage from './pages/admin/AtcCodeMaintenancePage';
import ClientDashboardPage from './pages/ClientDashboardPage';

// Management Pages
import StoreMaintenancePage from './pages/stores/StoreMaintenancePage';
import EmployeeManagement from './pages/EmployeeManagementPage';
import ProductCategoryManagementPage from './pages/ProductCategoryManagementPage';
import TableMaintenancePage from './pages/TableMaintenancePage';

// Sales & Order Pages
import SalesOrderPage from './pages/SalesOrder/SalesOrderPage';
import OrderPage from './components/OrderPage';
import OrderEntryPage from './pages/OrderEntryPage';
import BillOutPage from './pages/BillOutPage';
import RestaurantPOS from './pages/RestaurantPOS/RestaurantPOS';
import RestaurantGuard from './components/guards/RestaurantGuard';
import PosLayout from './pages/POS/PosLayout';
import ShiftStart from './pages/POS/ShiftStart';
import CashierDashboard from './pages/POS/CashierDashboard';
import OrderScreen from './pages/POS/OrderScreen';
import FloorPage from './pages/POS/FloorPage';
import PrinterSettingsPage from './pages/POS/PrinterSettingsPage';
import RestaurantMenuPage from './pages/RestaurantMenuPage';
import RestaurantInventoryPage from './pages/RestaurantInventoryPage';
import RestaurantMenuEngineeringPage from './pages/RestaurantMenuEngineeringPage';

//Acounting Module
import JournalEntriesListPage  from './pages/journalEntry/JournalEntriesList';
import NewJournalEntryPage  from './pages/journalEntry/NewJournalEntryPage';
import EditJournalEntryPage from './pages/journalEntry/EditJournalEntryPage';
import JournalEntryPrintPage from './pages/journalEntry/JournalEntryPrintPage';
import DisbursementsList from './pages/disbursements/DisbursementsList';
import NewDisbursementPage from './pages/disbursements/NewDisbursementPage';
import EditDisbursementPage from './pages/disbursements/EditDisbursementPage';
import DisbursementPrintPage from './pages/disbursements/DisbursementPrintPage';
import InvoiceListPage from './pages/invoice/InvoiceListPage';
import NewInvoicePage from './pages/invoice/NewInvoicePage';
import EditInvoicePage from './pages/invoice/EditInvoicePage';

import ChartOfAccountsList from './pages/chartOfAccounts/ChartOfAccountsList';
import NewChartOfAccountPage from './pages/chartOfAccounts/NewChartOfAccountPage';
import ChartOfAccountEdit from './pages/chartOfAccounts/ChartOfAccountEdit';
import ChartOfAccountsTypeMaintenancePage from './pages/chartOfAccountsTypes/ChartOfAccountsTypeMaintenancePage';
import GeneralLedgerPage from './pages/generalLedger/GeneralLedgerPage';


import TrialBalanceReport from './pages/financial-reports/TrialBalanceReport';


import ProfitLossReportPage from './pages/ProfitLossReportPage';

import AccountingDashboard from './components/AccountingDashboard';
import AccountingReportsPage from './components/AccountingReportsPage';
import AccountingFirmDashboard from './components/AccountingFirmDashboard';
import SalesOrderList from './pages/SalesOrder/SalesOrderList';
import SalesOrderRoute from './pages/SalesOrderRoute';


import AccountingClientsList from './pages/accountingClients/AccountingClientsList';
import NewAccountingClientPage from './pages/accountingClients/NewAccountingClientPage';
import AccountingClientDetails from './pages/accountingClients/AccountingClientDetails';
import TaxObligationsPage from './pages/accountingClients/TaxObligationsPage';

// Restaurant Service Pages
import TableServiceViewPage from './pages/TableServiceViewPage';
import CashierServiceViewPage from './pages/CashierServiceViewPage';

// Product Pages
import ProductsPage from './components/ProductPage';
import ProductCategoriesPage from './components/ProductCategoriesPage';

// Transaction Pages
import TransactionTypesPage from './components/TransactionTypesPage';
import BilledOutTransactions from './components/BiiledOutTransactions';

// Attendance Pages
import AttendancePage from './components/AttendancePage';
import PunchClockPage from './pages/PunchClockPage';
import AttendanceScreen from './pages/AttendanceScreen';
import AttendanceListPage from './pages/AttendanceListPage';


// Utility Pages
import CashierPage from './components/CashierPage';

import SupplierList from './pages/suppliers/SupplierList';
import PurchaseOrderList from './pages/PO/PurchaseOrderList';
import NewPurchaseOrderPage from './pages/PO/NewPurchaseOrderPage';
import PurchaseOrderDetailPage from './pages/PO/PurchaseOrderDetailPage';
import SalesOrderDetailPage from './pages/SalesOrder/SalesOrderDetailPage';
import ZReadingReportPage from './pages/ZReadingReportPage';

import PurchaseOrderReportPage from './pages/PurchaseOrderReportPage'
import PurchaseReturnReportPage from './pages/PurchaseReturnReportPage';

//Vendor
import VendorList from './pages/vendor/VendorList';
import EditVendorScreen from './pages/vendor/EditVendorScreen';

//Customers
import CustomerListPage from './pages/customer/CustomerListPage';
import CustomerCreatePage from './pages/customer/CustomerCreatePage';
import CustomerEditPage from './pages/customer/CustomerEditPage';

import InventoryList from './pages/inventory/InventoryList';

// Accounting Operations Module
import AODashboard from './components/ao/AODashboard';
import AOStaffMaintenance from './components/ao/AOStaffMaintenance';
import AOClientMaintenance from './components/ao/AOClientMaintenance';
import AOTransactionScreen from './components/ao/AOTransactionScreen';
import AOWorkingPaperReport from './components/ao/AOWorkingPaperReport';
import AOCollectionMonitor from './components/ao/AOCollectionMonitor';
import AORecurringScreen from './components/ao/AORecurringScreen';
import AODisbursementScreen from './pages/ao/AODisbursementScreen';
import AOCollectionScreen from './pages/ao/AOCollectionScreen';
import AODailyExpensesReport from './pages/ao/AODailyExpensesReport';
import AOLandTitleMonitor from './pages/ao/AOLandTitleMonitor';
import AODailyCollectionReport from './pages/ao/AODailyCollectionReport';
import AOPettyCashReport from './pages/ao/AOPettyCashReport';
import AOPettyCashScreen from './pages/ao/AOPettyCashScreen';

// Billing tenant type pages
import WaybillListPage from './pages/billing/WaybillListPage';
import WaybillEditPage from './pages/billing/WaybillEditPage';
import FFBillingListPage from './pages/billing/FFBillingListPage';
import FFBillingEditPage from './pages/billing/FFBillingEditPage';
import TruckingBillingListPage from './pages/billing/TruckingBillingListPage';
import TruckingBillingEditPage from './pages/billing/TruckingBillingEditPage';
import ARPage from './pages/billing/ARPage';
import ARAgingSummaryPage from './pages/billing/ARAgingSummaryPage';
import DailyCollectionPage from './pages/billing/DailyCollectionPage';
import {
  WaybillReportPage, FFBillingReportPage, TruckingBillingReportPage,
  DailyTripReportPage, PSACCBillingReportPage, PSACCBillingStripReportPage,
  BillingPettyCashReportPage,
} from './pages/billing/BillingReportPages';
import WaybillPrintPage from './pages/billing/WaybillPrintPage';
import FFBillingPrintPage from './pages/billing/FFBillingPrintPage';
import TruckingWaybillListPage  from './pages/billing/TruckingWaybillListPage';
import DailyWaybillReportPage from './pages/billing/DailyWaybillReportPage';
import DailyBillReportPage    from './pages/billing/DailyBillReportPage';
import DailyWaybillPrintPage  from './pages/billing/DailyWaybillPrintPage';
import DailyBillPrintPage     from './pages/billing/DailyBillPrintPage';
import RevenuePerVanPrintPage from './pages/billing/RevenuePerVanPrintPage';
import PSACCBillingPrintPage  from './pages/billing/PSACCBillingPrintPage';
import PSACCBillingStripPrintPage from './pages/billing/PSACCBillingStripPrintPage';
import PSACCSOARegistryPage from './pages/billing/PSACCSOARegistryPage';
import PSACCCombinedBillPrintPage from './pages/billing/PSACCCombinedBillPrintPage';
import ARPrintPage            from './pages/billing/ARPrintPage';
import DailyCollectionReportPage from './pages/billing/DailyCollectionReportPage';
import AgeingReportPage          from './pages/billing/AgeingReportPage';
import TruckingWaybillEditPage  from './pages/billing/TruckingWaybillEditPage';
import TruckingWaybillPrintPage from './pages/billing/TruckingWaybillPrintPage';
import TruckingBillingPrintPage from './pages/billing/TruckingBillingPrintPage';
import RevenuePerVanPage from './pages/billing/RevenuePerVanPage';
import UndeliveredFreightForwardingReportPage from './pages/billing/UndeliveredFreightForwardingReportPage';
import UndeliveredFreightForwardingPrintPage from './pages/billing/UndeliveredFreightForwardingPrintPage';
import UnitOfMeasurePage from './pages/billing/UnitOfMeasurePage';
import DriverMaintenancePage from './pages/billing/DriverMaintenancePage';
import DriverPayoutReportPage from './pages/billing/DriverPayoutReportPage';
import DriverPayoutPrintPage  from './pages/billing/DriverPayoutPrintPage';

import SystemSettingsPage from './pages/billing/SystemSettingsPage';

/* Property Management module
import LesseeDirectory from './pages/propertyManagement/LesseeDirectory';
import InvoiceListPM from './pages/propertyManagement/InvoiceList';
import PaymentLog from './pages/propertyManagement/PaymentLog';
import DailyCollectionReport from './pages/propertyManagement/DailyCollectionReport';
import MonthlyLesseeReport from './pages/propertyManagement/MonthlyLesseeReport';
import ArAgingReport from './pages/propertyManagement/ArAgingReport';
import LesseeLedger from './pages/propertyManagement/LesseeLedger';
import AccumulatedUnpaidReport from './pages/propertyManagement/AccumulatedUnpaidReport';
import InvoicePrintView from './pages/propertyManagement/InvoicePrintView';
import PmSettings from './pages/propertyManagement/PmSettings';*/

const Spin = ({ size = 'default' }) => {
  const px = size === 'large' ? 36 : 20;
  return (
    <div
      className="animate-spin"
      style={{
        width: px,
        height: px,
        border: '3px solid #d1d5db',
        borderTopColor: '#0d9488',
        borderRadius: '9999px',
      }}
    />
  );
};

const getTenantTypeFromUser = (user) => {
  if (!user) return null;

  const directTenantType = user.tenant_Type || user.tenantType;
  const userDataTenantType = user.userData?.tenant_Type || user.userData?.tenantType;

  return directTenantType || userDataTenantType || null;
};

const PUBLIC_LEGAL_PATHS = ['/terms', '/privacy'];

const RestaurantAttendanceRouteGuard = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (PUBLIC_LEGAL_PATHS.includes(location.pathname)) {
    return children;
  }

  if (loading || !isAuthenticated) {
    return children;
  }

  const tenantType = getTenantTypeFromUser(user);
  if (tenantType !== 'restaurant') {
    return children;
  }

  const role = user?.role;
  const isAttendanceScreen = location.pathname === '/attendance-screen';

  if (role === 'boh' && !isAttendanceScreen) {
    return <Navigate to="/attendance-screen" replace />;
  }

  if (role !== 'boh' && isAttendanceScreen) {
    return <Navigate to="/client/timesheet" replace />;
  }

  return children;
};

const AxiosAuthBridge = () => {
  const { getAccessToken, setAccessToken, refreshSession, logout, isRefreshRevoked } = useAuth();

  // Configure synchronously during render, not after
  // This ensures axios has the token accessors before
  // any child component fires API requests
  configureAxiosAuth({
    getAccessToken,
    setAccessToken,
    refreshSession,
    logout,
    isRefreshRevoked: () => isRefreshRevoked,
  });

  return null;
};

const SessionPoller = () => {
  useSessionPoller();
  return null;
};

const AppRoutes = ({ handleLogin, handleLogout }) => {
  const { isAuthReady } = useAuth();

  if (!isAuthReady) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Router future={{ v7_relativeSplatPath: true }}>
      {/* Public print routes — authenticated via printToken, no session guard */}
      <RestaurantAttendanceRouteGuard>
        <Routes>
        {/* Public print route — no ProtectedRoute wrapper */}
        <Route path="/billing/reports/waybill/:printToken" element={<WaybillPrintPage />} />
        <Route path="/billing/reports/trucking-waybill/:printToken" element={<TruckingWaybillPrintPage />} />
        <Route path="/billing/reports/trucking-billing/:printToken" element={<TruckingBillingPrintPage />} />
        {/* Public legal routes — no auth, no tenant resolution required */}
        <Route path="/terms" element={<LegalPage doc="terms" />} />
        <Route path="/privacy" element={<LegalPage doc="privacy" />} />

        {/* Auth Routes */}
        <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
        <Route path="/" element={<Navigate replace to="/login" />} />

        {/* Dashboard Routes */}

        <Route 
            path="/client-dashboard" 
            element={
              <ProtectedRoute>
                <ClientDashboardPage />
              </ProtectedRoute>
            } 
          />

        <Route path="/accounting-reports" element={<AccountingReportsPage />} />

        <Route 
            path="/superadmin-dashboard" 
            element={
              <ProtectedRoute requiredRole="superadmin">
                <SuperAdminDashboardPage />
              </ProtectedRoute>
            } 
          />

          <Route
            path="/superadmin/sessions"
            element={
              <ProtectedRoute requiredRole="superadmin">
                <SessionManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/atc-codes"
            element={
              <ProtectedRoute requiredRole={['admin', 'superadmin']}>
                <AtcCodeMaintenancePage />
              </ProtectedRoute>
            }
          />

        <Route path="/client/chart-of-accounts" element={<ChartOfAccountsList />} />
        <Route path="/client/chart-of-accounts/new" element={<NewChartOfAccountPage />} />
        <Route path='/client/chart-of-accounts/:id' element={<ChartOfAccountEdit/>} />
        <Route path='/client/chart-of-accounts-types' element={<ChartOfAccountsTypeMaintenancePage/>} />


        <Route path='/client/employees' element={<EmployeeManagement/>} />
        <Route path='/client/stores' element={<StoreMaintenancePage/>} />


        <Route path="/client/vendors" element={<VendorList />} />
        <Route path="/client/vendors/:vendorId/edit" element={<EditVendorScreen />} />


        <Route path="/client/accounting-clients" element={<AccountingClientsList />} />


        <Route path="/client/tax-obligation" element={<TaxObligationsPage />} />
        <Route path="/client/accounting-clients/new" element={<NewAccountingClientPage />} />
        <Route path="/client/accounting-clients/:id/edit" element={<AccountingClientDetails />} />
        <Route path="/client/accounting-clients/:id" element={<AccountingClientDetails />} />
        

        {/* Sales & Order Routes */}
        <Route path="/sales-order/new" element={<SalesOrderPage />} />
        <Route path="/sales-order" element={<SalesOrderRoute />} />
        <Route path='/sales-order/:id' element={<SalesOrderDetailPage/>} />
        <Route path="/order" element={<OrderPage onLogout={handleLogout} />} />
        <Route path="/orders/:storeId/:tableId" element={<OrderEntryPage />} />
        <Route path="/billout/:storeId/:tableId" element={<BillOutPage />} />


        <Route path='/journal-entry' element={<JournalEntriesListPage/>}/>

        <Route path='/journal-entry/new' element={<NewJournalEntryPage/>}/>
        <Route path='/journal-entry/:id/edit' element={<EditJournalEntryPage/>}/>
        <Route path='/journal-entry/:id' element={<EditJournalEntryPage/>}/>
        <Route path='/journal-entry/:id/print' element={<JournalEntryPrintPage/>}/>

        <Route path='/disbursements' element={<DisbursementsList/>}/>
        <Route path='/disbursements/new' element={<NewDisbursementPage/>}/>
        <Route path='/disbursements/:id' element={<EditDisbursementPage/>}/>
        <Route path='/disbursements/:id/print' element={<DisbursementPrintPage/>}/>
       
        

        <Route path='/client/general-ledger' element={<GeneralLedgerPage/>}/>


        <Route path='/invoice' element={<InvoiceListPage/>}/>
        <Route path='/invoice/new' element={<NewInvoicePage/>}/>
        <Route path='/invoice/:id' element={<EditInvoicePage/>}/>
        <Route path='/invoice/:id/edit' element={<EditInvoicePage/>}/>
        

        <Route path="/suppliers" element={<SupplierList />} />
        <Route path="/purchase-order" element={<PurchaseOrderList />} />
        <Route path='/purchase-order/new' element={<NewPurchaseOrderPage/>} />
        <Route path='/purchase-order/:id' element={<PurchaseOrderDetailPage/>} />
        <Route path='/financial-reports/trial-balance' element={<TrialBalanceReport/>} />
        <Route path='/reports/profit-loss' element={<ProfitLossReportPage/>} />
        <Route path='/reports/purchases' element={<PurchaseOrderReportPage/>} />
        <Route path='/reports/purchase-returns' element={<PurchaseReturnReportPage/>} />


        <Route path="/customer" element={<CustomerListPage />} />
        <Route path='/customer/new' element={<CustomerCreatePage/>} />
        <Route path='/customer/:id' element={<CustomerEditPage/>} />

        <Route path='/inventory' element={<InventoryList/>} />

        {/* Accounting Operations Routes */}
        <Route path='/ao' element={<AODashboard/>} />
        <Route path='/ao/staff' element={<AOStaffMaintenance/>} />
        <Route path='/ao/clients' element={<AOClientMaintenance/>} />
        <Route path='/ao/transactions' element={<AOTransactionScreen/>} />
        <Route path='/ao/report/working-paper' element={<AOWorkingPaperReport/>} />
        <Route path='/ao/report/collection' element={<AOCollectionMonitor/>} />
        <Route path='/ao/recurring' element={<AORecurringScreen/>} />
        <Route path='/ao/disbursements' element={<AODisbursementScreen/>} />
        <Route path='/ao/collections' element={<AOCollectionScreen/>} />
        <Route path='/ao/daily-report' element={<AODailyCollectionReport/>} />
        <Route path='/ao/expenses-report' element={<AODailyExpensesReport/>} />
        <Route path='/ao/land-titles' element={<AOLandTitleMonitor/>} />
        <Route path='/ao/petty-cash' element={<AOPettyCashScreen/>} />
        <Route path='/ao/petty-cash-report' element={<AOPettyCashReport/>} />
        {/* ── Billing tenant type routes ───────────────────────────────── */}
        {/* Waybill */}
        <Route path="/billing/waybill" element={<ProtectedRoute><WaybillListPage /></ProtectedRoute>} />
        <Route path="/billing/waybill/add" element={<ProtectedRoute><WaybillEditPage /></ProtectedRoute>} />
        <Route path="/billing/waybill/:id/edit" element={<ProtectedRoute><WaybillEditPage /></ProtectedRoute>} />

        {/* FF Billing */}
        <Route path="/billing/ff-billing" element={<ProtectedRoute><FFBillingListPage /></ProtectedRoute>} />
        <Route path="/billing/ff-billing/add" element={<ProtectedRoute><FFBillingEditPage /></ProtectedRoute>} />
        <Route path="/billing/ff-billing/:id/edit" element={<ProtectedRoute><FFBillingEditPage /></ProtectedRoute>} />

        {/* Trucking Waybill */}
        <Route path="/billing/trucking-waybill"          element={<ProtectedRoute><TruckingWaybillListPage /></ProtectedRoute>} />
        <Route path="/billing/trucking-waybill/add"      element={<ProtectedRoute><TruckingWaybillEditPage /></ProtectedRoute>} />
        <Route path="/billing/trucking-waybill/:id/edit" element={<ProtectedRoute><TruckingWaybillEditPage /></ProtectedRoute>} />

        {/* Trucking Billing */}
        <Route path="/billing/trucking-billing" element={<ProtectedRoute><TruckingBillingListPage /></ProtectedRoute>} />
        <Route path="/billing/trucking-billing/add" element={<ProtectedRoute><TruckingBillingEditPage /></ProtectedRoute>} />
        <Route path="/billing/trucking-billing/:id/edit" element={<ProtectedRoute><TruckingBillingEditPage /></ProtectedRoute>} />

        {/* Reused modules at /billing/* prefix */}
        <Route path="/billing/petty-cash" element={<ProtectedRoute><AOPettyCashScreen /></ProtectedRoute>} />
        <Route path="/billing/disbursement" element={<ProtectedRoute><DisbursementsList /></ProtectedRoute>} />
        <Route path="/billing/daily-expenses" element={<ProtectedRoute><AODailyExpensesReport /></ProtectedRoute>} />
        <Route path="/billing/daily-collection" element={<ProtectedRoute><DailyCollectionPage /></ProtectedRoute>} />
        <Route path="/billing/ar" element={<ProtectedRoute><ARPage /></ProtectedRoute>} />
        <Route path="/billing/ar-aging" element={<ProtectedRoute><ARAgingSummaryPage /></ProtectedRoute>} />

        {/* Billing Reports */}
        <Route path="/billing/reports/waybill" element={<ProtectedRoute><WaybillReportPage /></ProtectedRoute>} />
        <Route path="/billing/ff-billing/:id/print" element={<FFBillingPrintPage />} />
        <Route path="/billing/reports/ff-billing" element={<ProtectedRoute><FFBillingReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/trucking-billing" element={<ProtectedRoute><TruckingBillingReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/daily-trip" element={<ProtectedRoute><DailyTripReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/revenue-per-van" element={<ProtectedRoute><RevenuePerVanPage /></ProtectedRoute>} />
        <Route path="/billing/reports/undelivered-freight-forwarding" element={<ProtectedRoute><UndeliveredFreightForwardingReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/daily-waybill" element={<ProtectedRoute><DailyWaybillReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/daily-bill" element={<ProtectedRoute><DailyBillReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/daily-waybill" element={<ProtectedRoute><DailyWaybillPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/ar" element={<ProtectedRoute><ARPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/revenue-per-van" element={<ProtectedRoute><RevenuePerVanPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/undelivered-freight-forwarding" element={<ProtectedRoute><UndeliveredFreightForwardingPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/daily-bill" element={<ProtectedRoute><DailyBillPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/daily-collection" element={<ProtectedRoute><DailyCollectionReportPage /></ProtectedRoute>} />
        <Route path="/billing/ageing-report" element={<ProtectedRoute><AgeingReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/psacc-billing" element={<ProtectedRoute><PSACCBillingReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/psacc-billing" element={<ProtectedRoute><PSACCBillingPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/psacc-strip" element={<ProtectedRoute><PSACCBillingStripReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/psacc-strip" element={<ProtectedRoute><PSACCBillingStripPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/psacc-soa-registry" element={<ProtectedRoute><PSACCSOARegistryPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/psacc-combined/:billId" element={<ProtectedRoute><PSACCCombinedBillPrintPage /></ProtectedRoute>} />
        <Route path="/billing/reports/petty-cash" element={<ProtectedRoute><BillingPettyCashReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/driver-payout" element={<ProtectedRoute><DriverPayoutReportPage /></ProtectedRoute>} />
        <Route path="/billing/reports/print/driver-payout" element={<ProtectedRoute><DriverPayoutPrintPage /></ProtectedRoute>} />
        {/* Billing Setup */}
        <Route path="/billing/setup/unit-of-measure" element={<ProtectedRoute><UnitOfMeasurePage /></ProtectedRoute>} />
        <Route path="/billing/setup/drivers" element={<ProtectedRoute><DriverMaintenancePage /></ProtectedRoute>} />
        {/* Restaurant Service Routes */}
        <Route path="/table-service" element={<TableServiceViewPage storeId="67d6c5ac07b25cdd2606fa33" />} />
        <Route path="/cashier-service" element={<CashierServiceViewPage storeId="67d6c5ac07b25cdd2606fa33" />} />

        {/* Product Routes */}
        <Route path="/client/products" element={<ProductsPage />} />

        {/* Transaction Routes */}
        <Route path="/transaction-types" element={<TransactionTypesPage />} />
        <Route path="/client/transactions" element={<BilledOutTransactions />} />


        <Route path="reports/z-reading" element={<ZReadingReportPage />} />

        {/* Attendance & Time Tracking */}
        <Route path="/client/attendance" element={<PunchClockPage />} />
        <Route path="/client/timesheet" element={<AttendancePage />} />
        <Route path="/attendance-list" element={
          <ProtectedRoute>
            <AttendanceListPage />
          </ProtectedRoute>
        } />
        <Route path="/attendance-screen" element={<AttendanceScreen />} />
        <Route path="/restaurant-pos" element={<RestaurantPOS />} />

        {/* ── Restaurant POS (new /pos/* namespace) ───────────────────── */}
        <Route path="/pos" element={<RestaurantGuard><PosLayout /></RestaurantGuard>}>
          <Route index element={<ShiftStart />} />
          <Route path="shift-start" element={<ShiftStart />} />
          <Route path="floor" element={<FloorPage />} />
          <Route path="cashier" element={<CashierDashboard />} />
          <Route path="orders" element={<OrderScreen />} />
          <Route path="settings/printer" element={<PrinterSettingsPage />} />
        </Route>

        {/* ── Restaurant admin routes (portal auth, no POS cashier JWT) ── */}
        <Route path="/pos/menu" element={<RestaurantMenuPage />} />
        <Route path="/pos/inventory" element={<RestaurantInventoryPage />} />
        <Route path="/pos/menu-engineering" element={<RestaurantMenuEngineeringPage />} />

        {/* Utility Routes */}
        <Route path="/cashier" element={<CashierPage />} />
        <Route path="/billing/settings" element={<SystemSettingsPage />} />

        {/* ── Property Management module routes ─────────────────────── 
        <Route path="/property/lessees"                        element={<ProtectedRoute><LesseeDirectory /></ProtectedRoute>} />
        <Route path="/property/invoices"                       element={<ProtectedRoute><InvoiceListPM /></ProtectedRoute>} />
        <Route path="/property/payments"                       element={<ProtectedRoute><PaymentLog /></ProtectedRoute>} />
        <Route path="/property/reports/daily-collection"       element={<ProtectedRoute><DailyCollectionReport /></ProtectedRoute>} />
        <Route path="/property/reports/monthly-lessee"         element={<ProtectedRoute><MonthlyLesseeReport /></ProtectedRoute>} />
        <Route path="/property/reports/ar-aging"               element={<ProtectedRoute><ArAgingReport /></ProtectedRoute>} />
        <Route path="/property/reports/lessee-ledger"          element={<ProtectedRoute><LesseeLedger /></ProtectedRoute>} />
        <Route path="/property/reports/accumulated-unpaid"     element={<ProtectedRoute><AccumulatedUnpaidReport /></ProtectedRoute>} />
        <Route path="/property/invoices/:id/print"             element={<InvoicePrintView />} />
        <Route path="/property/settings"                       element={<ProtectedRoute><PmSettings /></ProtectedRoute>} />*/}
        </Routes>
      </RestaurantAttendanceRouteGuard>
    </Router>
  );
};

// App-wide query client for report pages (segment/period-keyed auto-refetch).
// POS keeps its own separate QueryClientProvider (pages/POS/PosLayout.jsx) scoped
// to that subtree; this one covers everything else, including accounting reports.
const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
  };

  return (
    <div className="App">
      <ToastProvider />
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AxiosAuthBridge />
          <SessionPoller />
          <AppRoutes handleLogin={handleLogin} handleLogout={handleLogout} />
        </AuthProvider>
      </QueryClientProvider>
    </div>
  );
}

export default App;
